import { Check, CheckSquare, Square, X, XCircle } from "lucide-react";

interface BulkActionBarProps {
  readonly selectedCount: number;
  readonly totalCount: number;
  readonly onSelectAll: () => void;
  readonly onClearSelection: () => void;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly disabled?: boolean;
}

/**
 * BulkActionBar - Floating action bar for bulk operations on submissions
 *
 * Displays when one or more submissions are selected. Provides:
 * - Selection count indicator with clear button
 * - Select all / Clear selection toggle
 * - Bulk approve and reject actions with icons
 *
 * Styled to match BulkActionsToolbar for visual consistency.
 */
export function BulkActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onApprove,
  onReject,
  disabled = false,
}: BulkActionBarProps) {
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50">
      <div className="bg-neutral-900/50 border border-neutral-800 backdrop-blur-md rounded-full shadow-2xl p-4 flex items-center gap-4">
        {/* Selection Count with Clear */}
        <div className="flex items-center gap-2 px-2">
          <span className="text-sm font-medium text-white whitespace-nowrap">{selectedCount} selected</span>
          <button
            type="button"
            onClick={onClearSelection}
            disabled={disabled}
            className="p-1 text-neutral-400 hover:text-white transition-colors rounded-full hover:bg-neutral-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-neutral-700" aria-hidden="true" />

        {/* Select All Toggle */}
        <button
          type="button"
          onClick={allSelected ? onClearSelection : onSelectAll}
          disabled={disabled}
          className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-neutral-700/50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={allSelected ? "Clear selection" : "Select all"}
        >
          {allSelected ? <CheckSquare className="w-4 h-4 text-lumi-accent" /> : <Square className="w-4 h-4" />}
          <span>{allSelected ? "Deselect All" : "Select All"}</span>
        </button>

        {/* Divider */}
        <div className="h-8 w-px bg-neutral-700" aria-hidden="true" />

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReject}
            disabled={disabled || selectedCount === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-600/20 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Reject ${selectedCount} items`}
          >
            <XCircle className="w-4 h-4" />
            <span>Reject</span>
          </button>

          <button
            type="button"
            onClick={onApprove}
            disabled={disabled || selectedCount === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-400 hover:text-green-300 hover:bg-green-600/20 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Approve ${selectedCount} items`}
          >
            <Check className="w-4 h-4" />
            <span>Approve</span>
          </button>
        </div>
      </div>
    </div>
  );
}
